import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserSessionsResultItem, ListUserSessionParamsV3, StaticFilterDisjunction } from 'interfaces/services';

export interface SessionState {
  items: UserSessionsResultItem[];
  startRequested: Date | null;
  hasMore: boolean;
  startHasMore: boolean;
  isLoading: boolean;
  error: string | null;
  lastRequestKey: string | null;
}

interface SessionsState {
  sessions: { [key: string]: SessionState };
  itemsPerPage: number;
}

const initialState: SessionsState = {
  sessions: {},
  itemsPerPage: 50
};

export const sessionListSlice = createSlice({
  name: 'sessionList',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<{ key: string, lastRequestKey: string }>) => {
      const { key, lastRequestKey } = action.payload;
      if (!state.sessions[key]) {
        state.sessions[key] = {
          items: [],
          startRequested: null,
          hasMore: true,
          startHasMore: true,
          isLoading: true,
          error: null,
          lastRequestKey: null
        };
      } else {
        state.sessions[key].isLoading = true;
        state.sessions[key].error = null;
        state.sessions[key].lastRequestKey = lastRequestKey;
      }
    },
    updateHasMore: (state, action: PayloadAction<{ key: string, hasMore: boolean }>) => {
      const { key, hasMore } = action.payload;
      state.sessions[key].hasMore = hasMore;
    },
    updateStartHasMore: (state, action: PayloadAction<{ key: string, startHasMore: boolean }>) => {
      const { key, startHasMore } = action.payload;
      state.sessions[key].startHasMore = startHasMore;
    },
    sessionsLoaded: (state, action: PayloadAction<{
      key: string;
      items: UserSessionsResultItem[];
      startRequested: Date | null;
      startHasMore: boolean;
      hasMore: boolean;
      isForceRefresh?: boolean;
      lastRequestKey: string;
    }>) => {
      const { key, items, startRequested, hasMore, startHasMore, isForceRefresh, lastRequestKey } = action.payload;
      const existingItems = !isForceRefresh ? state.sessions[key]?.items ?? [] : [];
      const lastItemStart = existingItems.length > 0 ? existingItems[existingItems.length - 1].scheduled_start : null;
      
      // Filter out duplicates when appending new items
      const currentSessionIds = new Set(existingItems.map(x => x.session_id));
      const newItems = (lastItemStart == null ? items : items.filter(x => !currentSessionIds.has(x.session_id)));

      state.sessions[key] = {
        items: [...existingItems, ...newItems],
        startRequested,
        hasMore,
        startHasMore,
        isLoading: false,
        error: null,
        lastRequestKey
      };
    },
    loadingFailed: (state, action: PayloadAction<{ key: string; error: string }>) => {
      const { key, error } = action.payload;
      if (!state.sessions[key]) {
        state.sessions[key] = {
          items: [],
          startRequested: null,
          startHasMore: false,
          hasMore: false,
          isLoading: false,
          error: null,
          lastRequestKey: null
        };
      } else {
        state.sessions[key].isLoading = false;
        state.sessions[key].error = error;
        state.sessions[key].lastRequestKey = null;
      }
    },
    clearSessions: (state, action: PayloadAction<{ key: string }>) => {
      const { key } = action.payload;
      delete state.sessions[key];
    },
    clearAllSessions: (state) => {
      state.sessions = {};
    }
  },
});

export const { 
  startLoading,
  sessionsLoaded,
  loadingFailed,
  clearSessions,
  clearAllSessions,
  updateHasMore,
  updateStartHasMore
} = sessionListSlice.actions;


export interface RefreshState {
  forceRefresh: number
}
  
const refreshInitialState: RefreshState = {
  forceRefresh: 0
};

export const refreshSlice = createSlice({
  name: 'refresh',
  initialState: refreshInitialState ,
  reducers: {
    updateForceRefresh: (state) => {
      state.forceRefresh += 1
    }
  },
});

export const { updateForceRefresh } = refreshSlice.actions;